import {confettiAnimationConfig} from './ConfettiAnimation.config';
import {DI_TOKENS} from '@/di/di.tokens';
import {Application, Assets, Container, ContainerChild, Sprite, Texture} from 'pixi.js';

import {injected} from 'brandi';

import {IDimensions} from '@specials/gcs-engine';
import {ParticleEmitter, SpawnShapeType} from '@specials/particle-emitter';

import {Config} from '@/types';
import {GameObjectUtils} from '@/utils/GameObjectUtils';
import {RealRandom} from '@/utils/random/RealRandom';

const MIN_INTERVAL_TIME = 800;
const MAX_INTERVAL_TIME = 1900;

export class ConfettiAnimation {
  private app: Application | null;
  private rootContainer: Container;
  private containerNode: HTMLElement;

  private confettiTexturePool: Texture[];
  private emitter: ParticleEmitter;

  constructor(private readonly config: Config, private readonly random: RealRandom) {}

  public async create(containerNode: HTMLElement): Promise<void> {
    const confettiSpritesheet = await Assets.load(this.getFullPath('confetti_set-2024-12-12-07-21-46.json'));
    this.confettiTexturePool = GameObjectUtils.createFramesFromSpriteSheet(confettiSpritesheet);

    this.containerNode = containerNode;
    const {width, height} = containerNode.getBoundingClientRect();

    this.app = new Application();
    await this.app.init({
      preference: 'webgl',
      width,
      height,
      background: 0x000000,
      resolution: 1,
      autoDensity: true,
      autoStart: true,
      antialias: true,
      resizeTo: this.containerNode,
      backgroundAlpha: 0,
    });

    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.style.pointerEvents = 'none';
    containerNode.appendChild(canvas);

    // это нужно для локального использования браузерного расширения для пикси
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (globalThis as any).__PIXI_APP__ = this.app;

    this.rootContainer = this.app.stage;

    this.emitter = new ParticleEmitter<ContainerChild>(this.rootContainer, this.createConfetti, {
      emitterConfig: {
        spawnInterval: {
          min: MIN_INTERVAL_TIME,
          max: MAX_INTERVAL_TIME,
        },
        spawnParticlesPerWave: 20,
        autoStart: true,
      },
      particleConfig: {
        ...confettiAnimationConfig(),
        spawnShape: {
          shape: {
            type: SpawnShapeType.Rectangle,
            x: width * 0.15,
            y: height * 0.1,
            width: width * 0.85,
            height: height * 0.7,
          },
          isGroupWave: true,
        },
      },
    });
  }

  public destroy(): void {
    this.emitter.destroy();

    this.app?.destroy();
  }

  private createConfetti = (): Sprite => {
    const sprite = new Sprite(this.random.choice(this.confettiTexturePool));
    sprite.anchor.set(0.5);
    return sprite;
  };

  private getFrameDimensions(): IDimensions {
    if (!this.app) return {width: 0, height: 0};

    return {
      width: this.app.renderer.width,
      height: this.app.renderer.height,
    };
  }

  private getFullPath(relativePath: string): string {
    return `${this.config.cdnUrl}/static/invest24/${relativePath}`;
  }
}

injected(ConfettiAnimation, DI_TOKENS.config, DI_TOKENS.realRandom);
